// libraries
import { DealerDecisionStatus } from '@makemydeal/dr-dash-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { CASH } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors, initActionTypes, type ReduxNext } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as offerReduxSelectors from '../selectors/offerRedux';

// consts/enums
import { UPDATED_CREDIT_DECISION_LENDER, UPDATED_CREDIT_DECISION_STATUS } from '../actionTypes/offerActionTypes';
import { GET_LENDER_DECISIONS_SUCCESS } from '../actionTypes/fullLenderDecisionsActionTypes';
import { CREDIT_APP_SEARCH_MERGE_DEAL } from '../actionTypes/creditAppSearchActionTypes';

// actions
import { updateCreditDecisionEnabled, updateCreditDecisionStatus } from '../actions/offerRedux';
import { fetchLenderDecisions } from '../actions/fullLenderDecisionsActionCreators';

const { INIT_PENCIL_SUCCESS, INIT_SUCCESS } = initActionTypes;

export const middleware = (store: any) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    const state = store.getState();

    switch (action.type) {
        case INIT_SUCCESS:
        case CREDIT_APP_SEARCH_MERGE_DEAL: {
            const isEnableMVLenderDecisions = featureToggleSelectors.isEnableMVLenderDecisions(state);
            const offerType = offerReduxSelectors.getCurrentOfferType(state);

            // Skip fetching lender decisions on cash deals
            if (isEnableMVLenderDecisions && offerType !== CASH) {
                store.dispatch(fetchLenderDecisions());
            }
            break;
        }
        case GET_LENDER_DECISIONS_SUCCESS: {
            const decisionsPayload = action.payload?.lenderDecisions ?? [];
            // AC7/F285110: we use the first decision's financeMethod — log if they differ (US1938864)
            const uniqueFinanceMethods = [
                ...new Set(decisionsPayload.map((d: { financeMethod?: string }) => d.financeMethod).filter(Boolean))
            ];
            if (uniqueFinanceMethods.length > 1) {
                logNewRelicPageAction('MV:Lender Decisions', {
                    type: 'mixedFinanceMethods',
                    financeMethods: uniqueFinanceMethods,
                    dealerId: dealerSelectors.getDealerId(state),
                    dealXgId: offerReduxSelectors.getDealXgId(state),
                    isRouteOneDealer: dealerSelectors.isCreditProviderRouteOne(state),
                    routeOnePartyId: dealerSelectors.getRouteOnePartyId(state),
                    partnerDealerId: dealerSelectors.getPartnerDealerId(state)
                });
            }

            const creditDecisionLender = offerReduxSelectors.getCreditDecisionLender(state);
            const lenderDecisions = action.payload?.lenderDecisions ?? [];
            const currentStatus = offerReduxSelectors.getCreditDecisionStatus(state);
            const decision = lenderDecisions.find((d: { lenderId: string }) => d.lenderId === creditDecisionLender?.id);
            const officialStatuses = [
                DealerDecisionStatus.APPROVED,
                DealerDecisionStatus.CONDITIONALLY_APPROVED,
                DealerDecisionStatus.DECLINED
            ];
            const isOfficial = decision?.status && officialStatuses.includes(decision.status);

            if (isOfficial && decision.status !== currentStatus) {
                store.dispatch(updateCreditDecisionStatus(decision.status));
            }
            break;
        }
        case INIT_PENCIL_SUCCESS:
        case UPDATED_CREDIT_DECISION_LENDER:
        case UPDATED_CREDIT_DECISION_STATUS: {
            const creditDecisionLender = offerReduxSelectors.getCreditDecisionLender(state);
            const creditDecisionStatus = offerReduxSelectors.getCreditDecisionStatus(state);
            const previousCreditDecisionEnabledStatus = offerReduxSelectors.getCreditDecisionEnabled(state);
            const offerType = offerReduxSelectors.getCurrentOfferType(state);

            const checkLenderStatus = ['Approved', 'ConditionallyApproved', 'Declined'];
            const isChecked = Boolean(creditDecisionLender) && checkLenderStatus.includes(creditDecisionStatus as string);

            if (previousCreditDecisionEnabledStatus !== isChecked) {
                return store.dispatch(updateCreditDecisionEnabled(isChecked, false, offerType));
            }
            break;
        }
    }
};
